import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import { Add as AddIcon, AccountCircle, Menu as MenuIcon, Logout as LogoutIcon, BugReport as BugReportIcon, Cable as CableIcon, Science as ScienceIcon, Analytics as AnalyticsIcon, LinkOff as LinkOffIcon, InfoOutlined as InfoIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, useUser } from '../../store';
import { LightMode, DarkMode } from '@mui/icons-material';
import { useThemeMode } from '../../lib/themes/ThemeProvider';
import { useConnectionWizard } from '../../features/device/ConnectionWizard';
import { useDeviceStore } from '../../store/useDeviceStore';
import { deviceService } from '../../services/device';
import { UploadQueueBadge } from './UploadQueueBadge';
import { AboutDialog } from './AboutDialog';
import { LabSwitcher } from '../../features/labs/LabSwitcher';
import { useTheme } from '@mui/material/styles';
import { useStartMeasurement } from '../../features/measurement/hooks/useStartMeasurement';
import { features } from '../../config/features';

export function AppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const theme = useTheme();

  const user = useUser();
  const { logout } = useAuthStore();
  const { mode, toggleTheme } = useThemeMode();
  const { open: openConnectionWizard } = useConnectionWizard();
  const deviceStore = useDeviceStore();
  const { startMeasurement } = useStartMeasurement();

  const isDeviceConnected = deviceStore.connectionState === 'connected' || deviceStore.connectionState === 'busy';
  const reconnectAttemptFailed = deviceStore.reconnectAttemptFailed;

  // compute a user‑friendly label for the device (nickname/serial/uid)
  const deviceLabel = deviceStore.getDeviceLabel();

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate('/login');
  };

  const handleDeviceChipClick = () => {
    if (window.confirm('Disconnect the device?')) {
      deviceService.disconnect();
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const logoSrc = mode === 'dark' ? '/ediphor-logo-light.svg' : '/ediphor-logo.svg';

  const allNavigationLinks = [
    { label: 'Dashboard', path: '/' },
    { label: 'Tests', path: '/tests' },
    { label: 'User Management', path: '/users', roles: ['admin', 'supervisor', 'user-manager'] },
    { label: 'Labs', path: '/labs', roles: ['admin', 'lab_manager'] },
    { label: 'Devices', path: '/devices', roles: ['admin', 'supervisor'] },
  ];

  // Filter navigation links based on user roles — fall back to user.role for stale sessions
  const userNavRoles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
  const navigationLinks = allNavigationLinks.filter(
    (link) => !link.roles || userNavRoles.some((r) => link.roles!.includes(r))
  );

  return (
    <MuiAppBar 
      position="sticky" 
      elevation={1}
      sx={{
        backgroundImage: theme.customHeader.backgroundImage,
        backgroundSize: theme.customHeader.backgroundSize,
        backgroundPosition: theme.customHeader.backgroundPosition,
        backgroundRepeat: theme.customHeader.backgroundRepeat,
        overflow: theme.customHeader.overflow,
      }}
    >
      <Toolbar>
        {/* Logo and Company Name */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 4 }}
          onClick={() => navigate('/')}
        >
          <Box
            component="img"
            src={logoSrc}
            alt="Ediphor"
            sx={{ height: 32, marginRight: 1 }}
            onError={(e) => {
              // Fallback if logo not available
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            LabCloud
          </Typography>
        </Box>

        {/* Main Navigation Tabs - Desktop */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          {navigationLinks.map((link) => (
            <Button
              key={link.path}
              color="inherit"
              onClick={() => navigate(link.path)}
              sx={{
                fontWeight: isActive(link.path) ? 600 : 400,
                borderBottom: isActive(link.path) ? '2px solid white' : 'none',
                '&:focus, &.Mui-focusVisible': {
                  outline: 'none',
                  backgroundColor: 'transparent',
                },
              }}
            >
              {link.label}
            </Button>
          ))}
        </Box>

        {/* Spacer for mobile to push hamburger to right */}
        <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }} />

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="top"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          <Box sx={{ width: '100%', py: 1 }}>
            {/* Navigation Links */}
            {navigationLinks.map((link) => (
              <MenuItem
                key={link.path}
                onClick={() => {
                  navigate(link.path);
                  handleMobileMenuClose();
                }}
                selected={isActive(link.path)}
                sx={{ px: 2, py: 1.5 }}
              >
                {link.label}
              </MenuItem>
            ))}

            {/* Divider between navigation and actions */}
            <Box sx={{ my: 1, borderTop: '1px solid', borderColor: 'divider' }} />

            {/* Connection / New Test section in Mobile Menu */}
            {isDeviceConnected ? (
              <MenuItem disabled sx={{ px: 2, py: 1.5 }}>
                <CableIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="body2" color="success.main">
                  {deviceLabel}
                </Typography>
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  openConnectionWizard();
                  handleMobileMenuClose();
                }}
                sx={{
                  px: 2,
                  py: 1.5,
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              >
                <CableIcon sx={{ mr: 1 }} /> Connect the reader
              </MenuItem>
            )}

            {/* New Test - always available in mobile menu */}
            <MenuItem
              onClick={() => {
                startMeasurement();
                handleMobileMenuClose();
              }}
              sx={{
                px: 2,
                py: 1.5,
                color: 'secondary.main',
                fontWeight: 600,
              }}
            >
              <AddIcon sx={{ mr: 1 }} /> New Test
            </MenuItem>

            {/* Device Diagnostic Button in Mobile Menu (Admin only) */}
            {user?.role === 'admin' && (
              <MenuItem
                onClick={() => {
                  navigate('/device-test');
                  handleMobileMenuClose();
                }}
                sx={{
                  px: 2,
                  py: 1.5,
                  color: 'warning.main',
                  fontWeight: 600,
                }}
              >
                <BugReportIcon sx={{ mr: 1 }} /> Device Diagnostic
              </MenuItem>
            )}

            {/* Testing Harness Button in Mobile Menu (Researcher only) */}
            {features.enableResearchFeatures && userNavRoles.includes('researcher') && (
              <MenuItem
                onClick={() => {
                  navigate('/harness');
                  handleMobileMenuClose();
                }}
                sx={{
                  px: 2,
                  py: 1.5,
                  color: 'info.main',
                  fontWeight: 600,
                }}
              >
                <ScienceIcon sx={{ mr: 1 }} /> Testing Harness
              </MenuItem>
            )}

            {/* Divider before user actions */}
            <Box sx={{ my: 1, borderTop: '1px solid', borderColor: 'divider' }} />

            {/* Theme Toggle in Mobile Menu */}
            <MenuItem
              onClick={() => {
                toggleTheme();
                handleMobileMenuClose();
              }}
              sx={{ px: 2, py: 1.5 }}
            >
              <ListItemIcon>
                {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
              </ListItemIcon>
              <ListItemText>
                {mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </ListItemText>
            </MenuItem>

            {/* User Profile in Mobile Menu */}
            <MenuItem
              onClick={() => {
                navigate('/profile');
                handleMobileMenuClose();
              }}
              sx={{ px: 2, py: 1.5 }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>

            {/* Guide in Mobile Menu */}
            <MenuItem
              onClick={() => {
                navigate('/guide');
                handleMobileMenuClose();
              }}
              sx={{ px: 2, py: 1.5 }}
            >
              <ListItemIcon>
                <HelpOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Guide</ListItemText>
            </MenuItem>

            {/* About in Mobile Menu */}
            <MenuItem
              onClick={() => {
                setAboutOpen(true);
                handleMobileMenuClose();
              }}
              sx={{ px: 2, py: 1.5 }}
            >
              <ListItemIcon>
                <InfoIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>About</ListItemText>
            </MenuItem>

            {/* Logout in Mobile Menu */}
            <MenuItem
              onClick={() => {
                handleLogout();
                handleMobileMenuClose();
              }}
              sx={{ px: 2, py: 1.5 }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>

            {/* User Email Display */}
            <Box sx={{ px: 2, py: 2, mt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Logged in as
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                {user?.email || 'Not signed in'}
              </Typography>
            </Box>
          </Box>
        </Drawer>

        {/* Connection - show device chip when connected, or connect button when not */}
        {isDeviceConnected ? (
          <Chip
            icon={<CableIcon />}
            label={deviceLabel}
            color="success"
            size="small"
            onClick={handleDeviceChipClick}
            sx={{ 
              mr: 2, 
              fontWeight: 600, 
              display: { xs: 'none', sm: 'flex' },
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              }
            }}
          />
        ) : reconnectAttemptFailed ? (
          <Chip
            icon={<LinkOffIcon />}
            label="Reader unavailable"
            color="warning"
            onClick={openConnectionWizard}
            sx={{
              mr: 2,
              fontWeight: 600,
              display: { xs: 'none', sm: 'flex' },
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
          />
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CableIcon />}
            onClick={openConnectionWizard}
            sx={{ 
              mr: 2, 
              fontWeight: 600, 
              display: { xs: 'none', sm: 'flex' },
              bgcolor: theme.customHeader.buttonBgColor !== 'transparent' ? theme.customHeader.buttonBgColor : 'transparent',
            }}
          >
            Connect the reader
          </Button>
        )}

        {/* New Test - always available in the header between Connect and Device Diagnostic */}
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={startMeasurement}
          sx={{ 
            mr: 2, 
            fontWeight: 600, 
            display: { xs: 'none', sm: 'flex' },
            bgcolor: theme.customHeader.buttonBgColor !== 'transparent' ? theme.customHeader.buttonBgColor : 'transparent',
          }}
        >
          New Test
        </Button>

        {/* Device Diagnostic Button - Desktop (Admin only) */}
        {user?.role === 'admin' && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<BugReportIcon />}
            onClick={() => navigate('/device-test')}
            sx={{ 
              mr: 2, 
              fontWeight: 600, 
              display: { xs: 'none', sm: 'flex' }, 
              bgcolor: theme.customHeader.buttonBgColor,
              '&:hover': { bgcolor: theme.customHeader.buttonHoverBgColor }
            }}
          >
            Device Diagnostic
          </Button>
        )}

        {/* Testing Harness Button - Desktop (Researcher only) */}
        {features.enableResearchFeatures && userNavRoles.includes('researcher') && (
          <Button
            variant="outlined"
            color="info"
            startIcon={<ScienceIcon />}
            onClick={() => navigate('/harness')}
            sx={{
              mr: 2,
              fontWeight: 600,
              display: { xs: 'none', sm: 'flex' },
              bgcolor: theme.customHeader.buttonBgColor,
              '&:hover': { bgcolor: theme.customHeader.buttonHoverBgColor }
            }}
          >
            Testing Harness
          </Button>
        )}

        {/* Analysis Button - Desktop (Researcher + Admin) */}
        {features.enableResearchFeatures && (user?.roles?.includes('researcher') || user?.roles?.includes('admin')) && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AnalyticsIcon />}
            onClick={() => navigate('/analysis')}
            sx={{ 
              mr: 2, 
              fontWeight: 600, 
              display: { xs: 'none', sm: 'flex' }, 
              bgcolor: theme.customHeader.buttonBgColor,
              '&:hover': { bgcolor: theme.customHeader.buttonHoverBgColor }
            }}
          >
            Analysis
          </Button>
        )}

        {/* Hamburger Menu - Mobile (Right) */}
        <IconButton
          color="inherit"
          aria-label="Open navigation menu"
          onClick={handleMobileMenuOpen}
          sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}
        >
          <MenuIcon />
        </IconButton>

        {/* Lab Switcher — context indicator for multi-lab users, desktop only */}
        <Box sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}>
          <LabSwitcher />
        </Box>

        {/* Upload Queue Badge - Shows pending uploads */}
        <UploadQueueBadge />

        {/* Theme Toggle Button - Desktop Only */}
        <IconButton
          color="inherit"
          onClick={toggleTheme}
          disableRipple
          sx={{ 
            mr: 2, 
            display: { xs: 'none', md: 'flex' },
            '&:focus, &.Mui-focusVisible': {
              outline: 'none',
              backgroundColor: 'transparent',
            },
          }}
          aria-label="Toggle theme"
        >
          {mode === 'dark' && <DarkMode />}
          {mode === 'ediphor' && (
            <Box
              component="img"
              src="/ediphor-icon-light.svg"
              alt="Ediphor"
              sx={{ height: 24, width: 24 }}
            />
          )}
          {mode === 'light' && <LightMode />}
        </IconButton>

        {/* User Menu - Desktop Only */}
        <IconButton
          size="large"
          aria-label="user account"
          aria-controls="user-menu"
          aria-haspopup="true"
          onClick={handleUserMenuOpen}
          color="inherit"
          disableRipple
          sx={{
            display: { xs: 'none', md: 'flex' },
            '&:focus, &.Mui-focusVisible': {
              outline: 'none',
              backgroundColor: 'transparent',
            },
          }}
        >
          <AccountCircle />
        </IconButton>
        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
        >
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {user?.email || 'Not signed in'}
            </Typography>
          </MenuItem>
          <MenuItem onClick={() => navigate('/profile')}>Profile</MenuItem>
          <MenuItem
            onClick={() => {
              navigate('/guide');
              handleUserMenuClose();
            }}
          >
            Guide
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAboutOpen(true);
              handleUserMenuClose();
            }}
          >
            About
          </MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
        <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
      </Toolbar>
    </MuiAppBar>
  );
}
